import { RocheUIFooter } from '@repo/roche-ui';
import { DateService } from '@rocheglobal/component-library-dot-com/src/services/date/index';
import React, { ReactNode, useEffect, useState } from 'react';
import { blokToComponent } from '../../helpers/blok-to-component';
import { BlokToComponentProps } from '../../helpers/blok-to-component/types';
import { useTranslation } from '../../hooks/use-translation';
import { LanguageService } from '../../services/language';
import { RocheRichtext as RocheRichTextType, RocheTextLink as RocheTextLinkType } from '../../types/global';

const assignProperties = (link, props) => ({ ...link, ...props });

const assignQuickLinks = (link: RocheTextLinkType) =>
  assignProperties(link, {
    type: 'footer-quick',
    'right-icon': 'chevron-right',
    size: 'huge',
    'icon-position': 'after',
  });

const assignHelpfulLinks = (link) => assignProperties(link, { type: 'footer-helpful' });
const assignSocialLinks = (link) =>
  assignProperties(link, {
    href: link.link,
    type: 'footer-social',
    label: useTranslation(link.left_icon),
  });
const assignServiceLinks = (link) => assignProperties(link, { type: 'footer-service' });

const assignText = (text: RocheRichTextType) => assignProperties(text, { type: 'footer', component: 'roche-richtext' });

type RocheFooterProps = BlokToComponentProps<{
  quick_links_title: string;
  slotted_quick_links: any[];
  helpful_links_title: string;
  slotted_helpful_links: any[];
  social_links_title: string;
  slotted_social_links: any[];
  service_links_title: string;
  slotted_service_links: any[];
  copyright: string;
  slotted_text: any[];
  slotted_stock_price: any[];
}>;

export const RocheFooter = ({ blok, getComponent }: RocheFooterProps) => {
  const [year, setYear] = useState<string>('');
  const [date, setDate] = useState<string>('');

  const slottedHelpfulLinks = blok.slotted_helpful_links.map(assignHelpfulLinks);
  const slottedQuickLinks = blok.slotted_quick_links.map((link) => assignQuickLinks(link));
  const slottedSocialLinks = blok.slotted_social_links.map(assignSocialLinks);
  const slottedServiceLinks = blok.slotted_service_links.map(assignServiceLinks);
  const slottedText = blok.slotted_text.map(assignText);

  const isRightToLeft = LanguageService.isRightToLeft();

  useEffect(() => {
    const fetchDate = async () => {
      const dateFromService = await DateService.formatDate(new Date(), 'dateNumeric');
      setDate(dateFromService);
      const yearFromService = await DateService.formatDate(new Date(), 'dateYear');
      setYear(yearFromService);
    };
    fetchDate();
  }, []);

  // once the authors have added the helpful links title in their storyblok spaces,
  // we can remove this translation and use blok.helpful_links_title directly
  const helpfulLinksTitleTranslation = useTranslation('helpful links');
  // if the authors have added the helpful links title in their storyblok spaces,
  // we will use it, otherwise we will use the already existing translation
  const helpfulLinksTitle = blok.helpful_links_title ? blok.helpful_links_title : helpfulLinksTitleTranslation;

  const text = blokToComponent(
    {
      getComponent,
      blok: slottedText[0],
    },
    'roche-footer-text',
  );

  const stockPrice = blokToComponent(
    {
      getComponent,
      blok: blok.slotted_stock_price[0],
    },
    'roche-stock-price',
  );

  const getLinkList = (linkList): ReactNode[] =>
    linkList.map((link) => {
      const linkItem = blokToComponent({ getComponent, blok: link }, 'roche-footer-link');
      // eslint-disable-next-line no-underscore-dangle
      return <li key={link._uid}>{linkItem}</li>;
    });

  return (
    <RocheUIFooter
      quickLinksTitle={blok.quick_links_title ?? ''}
      quickLinks={getLinkList(slottedQuickLinks)}
      helpfulLinksTitle={helpfulLinksTitle}
      helpfulLinks={getLinkList(slottedHelpfulLinks)}
      stockPrice={stockPrice}
      socialLinksTitle={blok.social_links_title ?? ''}
      socialLinks={getLinkList(slottedSocialLinks)}
      serviceLinksTitle={blok.service_links_title ?? ''}
      serviceLinks={getLinkList(slottedServiceLinks)}
      year={year}
      date={date}
      copyright={blok.copyright ?? ''}
      text={text}
      isRightToLeft={isRightToLeft}
    ></RocheUIFooter>
  );
};
